import React, { useState, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { FormContainer } from "../../shared/components/form/FormElements";
import FormHeader from "../../shared/components/form/FormHeader";
import { Col, Container, Row, Tab } from "react-bootstrap";
import { ButtonGroup } from "../../shared/components/Button";
import { Card, CardBody } from "@/shared/components/Card";
import { TabsWrap } from "@/shared/components/Tabs";
import styled from "styled-components";
import { scrollbarStyles } from "../../utils/palette";
import { useRef } from "react";
import { postApi } from "../../api/getUser";
import {
  getDistanceFactor,
  getEditUsers,
  getImgString,
  odoValidation,
  uploadDocsToS3,
} from "../../shared/helpers";

import getUnixTime from "date-fns/getUnixTime";
import { addMsg } from "../../features/snackbar/snackbarSlice";
import {
  updateExpensesData,
  updateFillupsData,
  updateIncomeData,
  updateTable,
} from ".";
import ErrorPopup from "../../shared/components/ErrorPopup";
import { s3Url } from "../../shared/constants";
import { Toggle } from "../VehicleInspection/InspectionSchedule/InspectionScheduleForm";
import ScheduledTripTabs from "./components/ScheduledTripTabs";
import TripTabs from "./components/TripTabs";

const TripsForm = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  const { currency, distance, consumption } = useSelector(
    (state) => state.units
  );

  const routeData = location?.state?.data;
  const rowData = routeData?.rowData;
  const type = location?.state?.type;

  const [fillupsData, setFillupsData] = useState([]);

  const [expensesData, setExpensesData] = useState([]);
  const [incomeData, setIncomeData] = useState([]);

  const vehiclesList = useSelector((state) => state.vehicles.list);
  const vehicles = useMemo(
    () => vehiclesList.filter((v) => v.veh_active === 1 && v.asset_type === 0),
    [vehiclesList]
  );
  const users = useSelector((state) => state.users.list);

  const { orgId, userId, roleId } = useSelector((state) => state.user);

  const methods = useForm();
  const { setError, clearErrors, setValue, watch } = methods;
  const { errors } = methods.formState;

  const vehicle = watch("vehicle");
  const driver = watch("driver");
  const startingOdo = watch("startingOdo");
  const endingOdo = watch("endingOdo");
  const depDate = watch("depDate");
  const arrDate = watch("arrDate");
  const depLocation = watch("depLocation");
  const arrLocation = watch("arrLocation");
  const comments = watch("comments");

  //scheduled fields
  const tripDriver = watch("tripDriver");
  const scheduledDeparture = watch("scheduledDeparture");
  const firstNotification = watch("firstNotification");
  const secondNotification = watch("secondNotification");
  const tripInstructions = watch("tripInstructions");
  const scheduledDepLocation = watch("scheduledDepLocation");
  const scheduledArrLocation = watch("scheduledArrLocation");

  const [tripDocs, setTripDocs] = useState([]);

  const [saveBtnLoading, setSaveBtnLoading] = useState(false);

  const [userOptions, setUserOptions] = useState([]);

  const [fromCancel, setFromCancel] = useState(true);

  const [newRecordData, setNewRecordData] = useState({
    type: "",
    vehicleId: "",
    userId: "",
    newRecordId: "",
    screenName: "",
    rowData: "",
  });

  const firstTimeEdit = useRef(false);

  //To toggle error popup while adding/editing form
  const [showErrPopup, setShowErrPopup] = useState(false);

  const [startTripLoading, setStartTripLoading] = useState(false);

  const [tripType, setTripType] = useState("unscheduled"); //scheduled(1) / not scheduled(0)
  const tripTypeOptions = [
    {
      id: 0,
      label: t("log_trip"),
      value: "unscheduled",
    },
    {
      id: 1,
      label: t("schedule_trip"),
      value: "scheduled",
    },
  ];

  const [showStartTripBtn, setShowStartTripBtn] = useState(false);

  const handleToggleChange = (value) => {
    clearErrors();
    if (type !== "edit" && value === "scheduled") {
      setValue("driver", null);
    }
    setTripType(value);
  };

  //Initial Effect
  useEffect(() => {
    if (type !== "edit") {
      setValue("depDate", new Date());
    }
  }, []);

  useEffect(() => {
    if (type !== "edit" && users?.length > 0) {
      setValue(
        "driver",
        users.find((u) => u.user_id === userId)
      );
    }
  }, [users]);

  //Update Fillups, Trips, Income row data
  useEffect(() => {
    (async () => {
      const { type, vehicleId, newRecordId, screenName } = newRecordData;
      if (newRecordId && screenName) {
        if (screenName === "fillups") {
          const apiData = {
            fillup_id: newRecordId,
            veh_id: vehicleId,
            querystring: "getsinglefillup",
          };
          const res = await postApi(apiData, "commonNew");
          if (res.success) {
            const rowData = res?.user_data[0];
            updateFillupsData(
              rowData,
              type,
              setFillupsData,
              {
                distance,
                consumption,
                currency,
              },
              t
            );
          }
        }

        if (screenName === "income") {
          const apiData = {
            income_id: newRecordId,
            veh_id: vehicleId,
            querystring: "getsingleincome",
            //tripIncomeIds: newRecordId,
          };
          const res = await postApi(apiData, "commonNew");
          if (res.success) {
            const rowData = res?.user_data[0];
            updateIncomeData(
              rowData,
              type,
              setIncomeData,
              {
                currency,
                distance,
              },
              t
            );
          }
        }

        if (screenName === "expenses") {
          console.log(newRecordData.expenseTaskIds);
          const apiData = {
            expense_id: newRecordId,
            veh_id: vehicleId,
            expense_task_ids: newRecordData.expenseTaskIds,
            querystring: "getsingleexpense",
          };

          const res = await postApi(apiData, "commonNew");
          if (res.success) {
            const rowData = res?.user_data[0];
            updateExpensesData(
              rowData,
              type,
              setExpensesData,
              {
                currency,
                distance,
              },
              t
            );
          }
        }
      }
    })();
  }, [newRecordData]);

  //Effect for edit
  useEffect(() => {
    (async () => {
      if (
        type === "edit" &&
        rowData &&
        vehicles?.length > 0 &&
        orgId
        //&&
        //users?.length > 0
      ) {
        firstTimeEdit.current = true;
        console.log(rowData);
        const {
          arr_date,
          arr_loc,
          arr_odo,
          dep_date,
          dep_loc,
          dep_odo,
          row_id,
          trip_by,
          trip_id,
          avg_speed,
          receipt_image_name,
          trip_expenses,
          trip_fillups,
          trip_income,
          trip_distance,
          veh_id,
          trip_time,
          primary_meter,
          comments,
          trip_status,
          sched_dep_date,
          sched_dep_loc,
          sched_arr_loc,
          sched_noti_1,
          sched_noti_2,
          sched_trip,
          sched_comments,
        } = rowData;

        //Set vehicle
        setValue(
          "vehicle",
          vehicles?.find((v) => v.veh_id === veh_id)
        );

        //Set user and user options
        (async () => {
          const editUsers = await getEditUsers(veh_id, trip_by, roleId, users);
          if (editUsers?.length > 0) {
            setUserOptions(editUsers);
            //checking if trip_by exists since it(assigned driver) may not be selected in scheduled trips
            if (trip_by) {
              const selectedUser = editUsers?.find(
                (u) => u.user_id === trip_by
              );
              setValue("driver", selectedUser);
            }
          }
        })();

        //Set trip type
        if (trip_status === 1) {
          setTripType("scheduled");
          setShowStartTripBtn(true);

          setValue("scheduledDeparture", new Date(sched_dep_date * 1000));
          setValue("scheduledDepLocation", sched_dep_loc);
          setValue("scheduledArrLocation", sched_arr_loc);
          setValue("tripInstructions", sched_comments);

          const firstNotiOptions = [
            { label: t("15_mins_before"), value: 1, mins: 15 },
            { label: t("30_mins_before"), value: 2, mins: 30 },
            { label: t("1_hour_before"), value: 3, mins: 60 },
            { label: t("3_hours_before"), value: 4, mins: 180 },
            { label: t("6_hours_before"), value: 5, mins: 360 },
            { label: t("12_hours_before"), value: 6, mins: 720 },
            { label: t("1_day_before"), value: 7, mins: 1440 },
          ];

          if (sched_noti_1) {
            setValue(
              "firstNotification",
              firstNotiOptions.filter((o) => o.value === sched_noti_1)
            );
          }
          if (sched_noti_1) {
            setValue(
              "secondNotification",
              firstNotiOptions.filter((o) => o.value === sched_noti_2)
            );
          }
        } else {
          setTripType("unscheduled");

          const distanceFactor = getDistanceFactor(primary_meter, distance);
          let depOdo = 0;
          let arrOdo = "";
          if (+dep_odo) {
            depOdo = +dep_odo / distanceFactor;
            depOdo = Number(depOdo).toFixed(2);
          }
          if (+arr_odo) {
            arrOdo = +arr_odo / distanceFactor;
            arrOdo = Number(arrOdo).toFixed(2);
          }

          setValue("depDate", new Date(dep_date * 1000));

          setValue("arrDate", arr_date ? new Date(arr_date * 1000) : null);
          setValue("depLocation", dep_loc);
          setValue("arrLocation", arr_loc);
          setValue("startingOdo", depOdo);
          setValue("endingOdo", arrOdo);
          setValue("comments", comments);

          //Logic to set Docs
          let imgNames = [];
          const editImgName = receipt_image_name;
          if (editImgName) {
            imgNames = editImgName.split(":::");

            const imgs = [];
            imgNames.map((imgName, i) => {
              imgs.push({
                existing: true,
                imgName: imgName,
                id: Date.now() + i,
                url: `${s3Url}/org_${orgId}/trips/${imgName}`,
              });
            });
            setTripDocs(imgs);
          }

          //Logic to update inner tables
          if (trip_fillups) {
            const fillupsApiData = {
              querystring: "gettripfillups",
              tripFillupIds: trip_fillups,
            };
            await updateTable(
              fillupsApiData,
              updateFillupsData,
              setFillupsData,
              {
                currency,
                distance,
                consumption,
              },
              t
            );
          }

          if (trip_income) {
            const incomeApiData = {
              querystring: "gettripIncome",
              tripIncomeIds: trip_income,
            };
            await updateTable(
              incomeApiData,
              updateIncomeData,
              setIncomeData,
              {
                currency,
                distance,
              },
              t
            );
          }

          if (trip_expenses) {
            const expensesApiData = {
              querystring: "gettripExpense",
              tripExpenseIds: trip_expenses,
            };

            await updateTable(
              expensesApiData,
              updateExpensesData,
              setExpensesData,
              { currency, distance },
              t
            );
          }
        }
      }
    })();
  }, [rowData, vehicles, orgId, users, roleId]);

  const handleStartTrip = async () => {
    //Validations
    if (!scheduledDeparture) {
      setError(
        "scheduledDeparture",
        {
          type: "required",
          message: t("required_err"),
        },
        { shouldFocus: true }
      );
      return;
    } else {
      clearErrors("scheduledDeparture");
    }

    if (!scheduledDepLocation) {
      setError(
        "scheduledDepLocation",
        {
          type: "required",
          message: t("required_err"),
        },
        { shouldFocus: true }
      );
      return;
    } else {
      clearErrors("scheduledDepLocation");
    }

    setStartTripLoading(true);

    let apiData = {
      operation: "update",
      insertTripAction: 2,
      querystring: "insert_trip",
      insertTripId: rowData?.trip_id,
      insertTripVehId: vehicle.veh_id,
      sync_version: "v2",
      source: "web",
      insertTripStatus: 1,
      insertTripSchedTrip: 1,
      insertTripTripBy: driver ? driver.user_id : "",
      insertTripSchedDepDate: getUnixTime(scheduledDeparture),
      insertTripSchedDepLoc: scheduledDepLocation,
      insertTripSchedArrLoc: scheduledArrLocation || "",
      insertTripSchedComments: tripInstructions || "",
      insertTripSchedNoti1: firstNotification ? firstNotification.value : 0,
      insertTripSchedNoti2: secondNotification ? secondNotification.value : 0,
    };

    const response = await postApi(apiData, "commonNew");

    setStartTripLoading(false);
    if (response.success === 1) {
      //if user clicks on cancel, then listing api should be called since trip data has been updated
      setFromCancel(false);
      setTripType("unscheduled");
      setShowStartTripBtn(false);
    } else {
      setShowErrPopup(true);
      return;
    }
  };

  const onSubmit = async () => {
    try {
      let tripStatus = 1; //(1=scheduled, 2=in progress, 3=completed)
      if (type !== "edit") {
        //add
        if (tripType === "unscheduled") {
          if (+endingOdo) {
            tripStatus = 3; //completed
          } else {
            tripStatus = 2; //in progress
          }
        }
      } else {
        //edit
        tripStatus = rowData.trip_status;
        if (tripType === "unscheduled") {
          if (+startingOdo >= 0 && !+endingOdo && rowData.trip_status !== 2) {
            tripStatus = 2; //in progress
          }
          if (+startingOdo >= 0 && +endingOdo && rowData.trip_status !== 3) {
            tripStatus = 3; //completed
          }
        }
      }

      if (tripStatus !== 1) {
        if (!driver) {
          setError(
            "driver",
            {
              type: "required",
              message: t("required_err"),
            },
            { shouldFocus: true }
          );
          return;
        } else {
          clearErrors("driver");
        }
      }

      const tripId =
        type === "edit"
          ? rowData.trip_id
          : `${vehicle.veh_id}_${Date.now()}`;

      const { imgString, imgsToS3 } = getImgString(
        tripDocs,
        tripId,
        userId,
        orgId,
        "trips"
      );

      let apiData = {
        operation: type === "edit" ? "update" : "create",
        insertTripAction: type === "edit" ? 2 : 1,
        querystring: "insert_trip",
        insertTripId: tripId,
        insertTripVehId: vehicle.veh_id,
        sync_version: "v2",
        source: "web",
        insertTripStatus: tripStatus,
        insertTripSchedTrip:
          type === "edit"
            ? rowData.sched_trip
            : tripType === "unscheduled"
            ? 0
            : 1,
        insertTripTripBy: driver ? driver.user_id : "",
      };

      if (tripStatus === 1) {
        //Scheduled Trip
        Object.assign(apiData, {
          insertTripSchedDepDate: getUnixTime(scheduledDeparture),
          insertTripSchedDepLoc: scheduledDepLocation,
          insertTripSchedArrLoc: scheduledArrLocation || "",
          insertTripSchedComments: tripInstructions || "",
          insertTripSchedNoti1: firstNotification ? firstNotification.value : 0,
          insertTripSchedNoti2: secondNotification
            ? secondNotification.value
            : 0,
        });
      } else {
        if ((+endingOdo && +startingOdo >= +endingOdo) || endingOdo === "0") {
          setError(
            "endingOdo",
            {
              type: "custom",
              message: t("ending_odo_less_than_starting_odo_err"),
            },
            { shouldFocus: true }
          );

          return;
        }

        //Odo Validation
        const distanceFactor = getDistanceFactor(
          vehicle.primary_meter,
          distance
        );
        //Starting Odo
        const startingOdoConverted = +startingOdo * distanceFactor;
        const startingOdoCheckApiData = {
          odo: startingOdoConverted,
          veh_id: vehicle.veh_id,
          datetocheck: getUnixTime(depDate),
          querystring: "get_around_dates_v3",
          check_from: "trips",
          check_id: type === "edit" ? rowData.trip_id : "",
        };
        const startingOdoValidationRes = await odoValidation(
          "startingOdo",
          startingOdoCheckApiData,
          setError,
          setSaveBtnLoading,
          t,
          distanceFactor,
          startingOdo
        );

        if (!startingOdoValidationRes) {
          return;
        }

        //Ending Odo
        let endingOdoConverted = 0;
        if (endingOdo) {
          endingOdoConverted = +endingOdo * distanceFactor;
          const endingOdoCheckApiData = {
            odo: endingOdoConverted,
            veh_id: vehicle.veh_id,
            datetocheck: getUnixTime(arrDate),
            querystring: "get_around_dates_v3",
            check_from: "trips",
            check_id: type === "edit" ? rowData.trip_id : "",
          };
          const endingOdoValidationRes = await odoValidation(
            "endingOdo",
            endingOdoCheckApiData,
            setError,
            setSaveBtnLoading,
            t,
            distanceFactor,
            endingOdo
          );

          if (!endingOdoValidationRes) {
            return;
          }
        }

        //Unscheduled Trip
        Object.assign(apiData, {
          insertTripDepDate: getUnixTime(depDate),
          insertTripDepOdo: startingOdoConverted,
          insertTripDepLoc: depLocation ? depLocation : "",
          insertTripArrDate: arrDate ? getUnixTime(arrDate) : 0,
          insertTripArrOdo: endingOdo ? endingOdoConverted : 0,
          insertTripArrLoc: arrLocation ? arrLocation : "",
          insertTripOdoUnit:
            vehicle.primary_meter === "Hours" ? "Hours" : distance,
          insertTripTripDistance: endingOdo
            ? endingOdoConverted - startingOdoConverted
            : 0,
          insertTripTripTime: arrDate ? getUnixTime(arrDate - depDate) : 0,
          insertTripTripFillups:
            fillupsData.length > 0
              ? fillupsData.map((row) => row.rowId).join(":::")
              : "",
          insertTripTripExpenses:
            expensesData.length > 0
              ? expensesData.map((row) => row.rowId).join(":::")
              : "",
          insertTripTripIncome:
            incomeData.length > 0
              ? incomeData.map((row) => row.rowId).join(":::")
              : "",
          insertTripCurrency: currency ? currency : "",
          insertTripReceiptImageName: imgString,
          insertTripComments: comments ? comments : "",
          insertAvgSpeed: type === "edit" ? rowData.avg_speed : "",
        });
      }

      setSaveBtnLoading(true);
      const snackbarMsg =
        type === "edit"
          ? t("record_edited_msg", { recordName: t("trip") })
          : t("record_added_msg", { recordName: t("trip") });
      const response = await postApi(apiData, "commonNew");

      if (response.success === 1) {
        if (imgsToS3?.length > 0) {
          const docsUploaded = await uploadDocsToS3(imgsToS3);
          if (docsUploaded) {
            setSaveBtnLoading(false);
            dispatch(addMsg(snackbarMsg));
            history.push("/trips", { fromCancel: false });
          }
        } else {
          setSaveBtnLoading(false);
          dispatch(addMsg(snackbarMsg));
          history.push("/trips", { fromCancel: false });
        }
      } else {
        setSaveBtnLoading(false);
        setShowErrPopup(true);
        return;
      }
    } catch (e) {
      setSaveBtnLoading(false);
      setShowErrPopup(true);
      return;
    }
  };
  return (
    <>
      {showErrPopup && (
        <ErrorPopup showPopup={showErrPopup} setShowPopup={setShowErrPopup} />
      )}
      <FormProvider {...methods}>
        <FormContainer onSubmit={methods.handleSubmit(onSubmit)}>
          <FormHeader
            handleCancel={() => history.push("/trips", { fromCancel })}
            //disableSave={!vehicle}
            isSaveLoading={saveBtnLoading}
            title={type === "edit" ? t("edit_trip") : t("add_trip")}
            rowData={rowData}
            onStartTrip={handleStartTrip}
            showStartTripBtn={showStartTripBtn}
            startTripLoading={startTripLoading}
            //handleCancel={handleCancel}
          />
          <Container>
            <Row>
              <Col xs={12} md={12} lg={12} xl={12}>
                <Card>
                  <CardBody
                    style={{
                      padding: 0,
                      //height: `${window.innerHeight - 150}px`,
                      //overflow: "auto",
                    }}
                  >
                    {type !== "edit" && (
                      <div className="py-3 d-flex justify-content-center ">
                        <ButtonGroup>
                          {tripTypeOptions.map((item) => (
                            <Toggle
                              className="m-0"
                              active={tripType === item.value}
                              disabled={type === "edit"}
                              key={item.id}
                              id={`${item.value}-toggle-button`}
                              type="radio"
                              variant="light"
                              name="radio"
                              value={item.value}
                              checked={tripType === item.value}
                              onChange={() => handleToggleChange(item.value)}
                            >
                              {item.label}
                            </Toggle>
                          ))}
                        </ButtonGroup>
                      </div>
                    )}
                    <TabsWrap>
                      {tripType === "unscheduled" ? (
                        <TripTabs
                          rowData={rowData}
                          type={type}
                          fillupsData={fillupsData}
                          setFillupsData={setFillupsData}
                          expensesData={expensesData}
                          setExpensesData={setExpensesData}
                          incomeData={incomeData}
                          setIncomeData={setIncomeData}
                          newRecordData={newRecordData}
                          setNewRecordData={setNewRecordData}
                          vehicle={vehicle}
                          driver={driver}
                          tripDocs={tripDocs}
                          setTripDocs={setTripDocs}
                          firstTimeEdit={firstTimeEdit}
                          errors={errors}
                          userOptions={userOptions}
                          setUserOptions={setUserOptions}
                          tripType={tripType}
                        />
                      ) : (
                        <ScheduledTripTabs
                          rowData={rowData}
                          type={type}
                          fillupsData={fillupsData}
                          setFillupsData={setFillupsData}
                          expensesData={expensesData}
                          setExpensesData={setExpensesData}
                          incomeData={incomeData}
                          setIncomeData={setIncomeData}
                          newRecordData={newRecordData}
                          setNewRecordData={setNewRecordData}
                          vehicle={vehicle}
                          driver={driver}
                          tripDocs={tripDocs}
                          setTripDocs={setTripDocs}
                          firstTimeEdit={firstTimeEdit}
                          errors={errors}
                          userOptions={userOptions}
                          setUserOptions={setUserOptions}
                          tripType={tripType}
                        />
                      )}
                    </TabsWrap>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </FormContainer>
      </FormProvider>
    </>
  );
};

export default TripsForm;

export const TabPane = styled(Tab.Pane)`
  ${scrollbarStyles};
  height: ${(p) =>
    p.$edit ? window.innerHeight - 182 : window.innerHeight - 252}px;
  overflow: auto;
  //padding: 1rem;
`;
